import { useState } from "react";
import { useAppStore } from "../store/useAppStore";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Textarea } from "./ui/textarea";
import { LeadStatus } from "../types";
import { toast } from "sonner";

interface AddLeadDialogProps {
  children: React.ReactNode;
}

export function AddLeadDialog({ children }: AddLeadDialogProps) {
  const [open, setOpen] = useState(false);
  const { addLead } = useAppStore();

  const [formData, setFormData] = useState({
    sellerName: "",
    phone: "",
    propertyAddress: "",
    askingPrice: "",
    motivation: "",
    leadSource: "Manual",
    status: "New Lead" as LeadStatus,
    notes: "",
    tags: [] as string[],
  });

  const availableTags = [
    "Pre-Foreclosure",
    "Tax Delinquent",
    "Vacant",
    "Tired Landlord",
    "Probate",
    "Absentee Owner",
    "Code Violation",
    "High Equity",
    "Needs Follow-Up",
    "Needs Skiptrace",
  ];

  const handleTagToggle = (tag: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter((t) => t !== tag)
        : [...prev.tags, tag],
    }));
  };

  const resetForm = () => {
    setFormData({
      sellerName: "",
      phone: "",
      propertyAddress: "",
      askingPrice: "",
      motivation: "",
      leadSource: "Manual",
      status: "New Lead",
      notes: "",
      tags: [],
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.sellerName) return;

    addLead({
      sellerName: formData.sellerName,
      phone: formData.phone,
      propertyAddress: formData.propertyAddress,
      askingPrice: formData.askingPrice ? Number(formData.askingPrice) : null,
      motivation: formData.motivation,
      leadSource: formData.leadSource,
      status: formData.status,
      notes: formData.notes,
      tags: formData.tags,
    });

    toast.success(`${formData.sellerName} added successfully`);
    setOpen(false);
    resetForm();
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => {
      setOpen(nextOpen);
      if (!nextOpen) resetForm();
    }}>
      <DialogTrigger render={children as React.ReactElement} />
      <DialogContent className="sm:max-w-[640px] bg-white max-h-[calc(100dvh-1rem)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Lead</DialogTitle>
          <DialogDescription>
            Capture the essentials now. You can refine status, notes, and tags later.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-5 py-2">
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
            Required fields are marked with *. Everything else is optional.
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="sellerName">Seller Name *</Label>
              <Input
                id="sellerName"
                required
                value={formData.sellerName}
                onChange={(e) => setFormData({ ...formData, sellerName: e.target.value })}
                placeholder="John Doe"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="(555) 123-4567"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="propertyAddress">Property Address</Label>
            <Input
              id="propertyAddress"
              value={formData.propertyAddress}
              onChange={(e) => setFormData({ ...formData, propertyAddress: e.target.value })}
              placeholder="123 Main St, City, ST 12345"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="askingPrice">Asking Price ($)</Label>
              <Input
                id="askingPrice"
                type="number"
                value={formData.askingPrice}
                onChange={(e) => setFormData({ ...formData, askingPrice: e.target.value })}
                placeholder="250000"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={formData.status} onValueChange={(val) => setFormData({ ...formData, status: val as LeadStatus })}>
                <SelectTrigger>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="New Lead">New Lead</SelectItem>
                  <SelectItem value="Contacted">Contacted</SelectItem>
                  <SelectItem value="Follow-Up">Follow-Up</SelectItem>
                  <SelectItem value="Appointment Set">Appointment Set</SelectItem>
                  <SelectItem value="Offer Made">Offer Made</SelectItem>
                  <SelectItem value="Under Contract">Under Contract</SelectItem>
                  <SelectItem value="Dead Lead">Dead Lead</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="motivation">Motivation</Label>
            <Input
              id="motivation"
              placeholder="Why are they selling?"
              value={formData.motivation}
              onChange={(e) => setFormData({ ...formData, motivation: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Initial Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Add context, timing, or next steps."
              rows={4}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <Label>Distress Tags</Label>
              <span className="text-xs text-slate-500">{formData.tags.length} selected</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {availableTags.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => handleTagToggle(tag)}
                  className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${
                    formData.tags.includes(tag)
                      ? "border-blue-200 bg-blue-100 font-medium text-blue-700"
                      : "border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
              Save Lead
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
